import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Divider,
    Grid,
    Chip,
    TextField,
} from '@mui/material';
import axios from 'axios';
import { internshipDomain, phonepeDomain } from '../util/domain';

const Payment = () => {
    const { registrationHashId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [registrationData, setRegistrationData] = useState(null);
    const [success, setSuccess] = useState('');
    const [couponCode, setCouponCode] = useState('');
    const [couponDetails, setCouponDetails] = useState(null);
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    const [couponError, setCouponError] = useState('');

    useEffect(() => {
        fetchRegistrationDetails();
    }, [registrationHashId]);
    console.log("registrationHashId", registrationHashId);
    console.log("workshop domain:", internshipDomain);
    console.log("phonepe domain:", phonepeDomain);

    const fetchRegistrationDetails = async () => {
        try {
            const response = await axios.get(`${internshipDomain}/api/registration/${registrationHashId}`);
            console.log("response :", response);
            if (response.data.success) {
                setRegistrationData(response.data.registration);
                handlePaymentSuccess(registrationHashId);
            }
        } catch (error) {
            setError('Failed to fetch registration details');
        } finally {
            setLoading(false);
        }
    };

    const handleCouponChange = (e) => {
        setCouponCode(e.target.value.toUpperCase());
        setCouponError('');
    };

    const validateCoupon = async () => {
        if (!couponCode) return;

        setValidatingCoupon(true);
        setCouponError('');

        try {
            const response = await axios.post(`${internshipDomain}/api/coupons/validate`, {
                code: couponCode,
                eventHashId: registrationData.event_hash_id,
                amount: registrationData.registration_fee
            });

            if (response.data.success) {
                setCouponDetails(response.data.coupon);
            }
        } catch (error) {
            setCouponDetails(null);
            setCouponError(error.response?.data?.message || 'Invalid coupon code');
        } finally {
            setValidatingCoupon(false);
        }
    };

    const handlePayment = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${phonepeDomain}/api/payment/initiate`, {
                registrationHashId,
                couponHashId: couponDetails?.hashId
            });

            if (response.data.payment) {
                navigate(`/event-pass/${response.data.payment.registration_hash_id}`);
                return;
            }

            if (response.data.success) {
                window.location.href = response.data.paymentUrl;
            }
        } catch (error) {
            setError('Payment initiation failed');
            setLoading(false);
        }
    };

    const handlePaymentSuccess = async (registrationHashId) => {
        try {
            const response = await axios.get(`${internshipDomain}/api/payment/paymentstatus/${registrationHashId}`);
            console.log("response :", response.data);
            if (response.data.success) {
                setSuccess('Payment successful! Redirecting to event pass...');
                setTimeout(() => {
                    navigate(`/event-pass/${registrationHashId}`);
                }, 2000);
            }
        } catch (error) {
            setError('Payment verification failed');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (!registrationData) {
        return (
            <Container maxWidth="sm">
                <Alert severity="error" sx={{ mt: 4 }}>
                    Registration details not found
                </Alert>
            </Container>
        );
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Container maxWidth="md">
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, mt: 1 }}>
                <img
                    src="/logo-vector.svg"
                    alt="Vector India"
                    style={{
                        height: '120px',
                        width: 'auto'
                    }}
                />
            </Box>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Payment Details
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" gutterBottom color="primary">
                        Event Details
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="h6">
                                    {registrationData.event_title}
                                </Typography>
                                <Chip
                                    label={registrationData.event_type}
                                    color={registrationData.event_type === 'WORKSHOP' ? 'primary' : 'secondary'}
                                    size="small"
                                />
                                <Chip
                                    label={registrationData.is_online ? 'Online' : 'Offline'}
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body1">
                                <strong>Venue:</strong> {registrationData.is_online ? 'Online Event' : registrationData.venue}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body1">
                                <strong>Registration Fee:</strong> ₹{registrationData.registration_fee}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body1">
                                <strong>{registrationData.event_type === 'WORKSHOP' ? 'Workshop' : 'Webinar'} Dates:</strong><br />
                                {formatDate(registrationData.event_start_date)} - {formatDate(registrationData.event_end_date)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" gutterBottom color="primary">
                        Student Details
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body1">
                                <strong>Name:</strong> {registrationData.name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body1">
                                <strong>Email:</strong> {registrationData.email}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body1">
                                <strong>Mobile:</strong> {registrationData.mobile}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body1">
                                <strong>College:</strong> {registrationData.college_name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body1">
                                <strong>Branch:</strong> {registrationData.branch}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body1">
                                <strong>Year:</strong> {registrationData.year}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" gutterBottom color="primary">
                        Payment Summary
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="body1">
                                <strong>Registration Fee:</strong> ₹{registrationData.registration_fee}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    fullWidth
                                    label="Coupon Code"
                                    value={couponCode}
                                    onChange={handleCouponChange}
                                    error={!!couponError}
                                    helperText={couponError}
                                    disabled={!!couponDetails}
                                />
                                {!couponDetails ? (
                                    <Button
                                        variant="outlined"
                                        onClick={validateCoupon}
                                        disabled={!couponCode || validatingCoupon}
                                        sx={{ minWidth: 100 }}
                                    >
                                        {validatingCoupon ? 'Checking...' : 'Apply'}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => {
                                            setCouponDetails(null);
                                            setCouponCode('');
                                        }}
                                        sx={{ minWidth: 100 }}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </Box>
                        </Grid>

                        {couponDetails && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="success.main">
                                        Coupon applied: {couponDetails.code}
                                        {couponDetails.discountType === 'PERCENTAGE'
                                            ? ` (${couponDetails.discountValue}% off)`
                                            : ` (₹${couponDetails.discountValue} off)`}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body1" color="error">
                                        <strong>Discount:</strong> -₹{couponDetails.discountAmount}
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <Typography variant="h6">
                                <strong>Total Amount:</strong> ₹{couponDetails ? couponDetails.finalAmount : registrationData.registration_fee}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handlePayment}
                        disabled={loading}
                        sx={{ minWidth: 200 }}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            couponDetails?.finalAmount === 0 ?
                                'Generate Event Pass' :
                                `Pay ₹${couponDetails ? couponDetails.finalAmount : registrationData.registration_fee}`
                        )}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default Payment; 